import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { trackSegment } from '~/components/SegmentAnalytics';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { TableViewContextType } from '~/enums/TableViewType';
import { api } from '~/redux/api/createApi';
import { CreateTableViewSet } from '~/redux/api/methods/tableViews';
import { MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE } from '~/types/modal.types';
import { Nullable } from '~/types/Nullable';
import { ERROR, SUCCESS } from '~/types/toast.types';

interface Props {
  context: TableViewContextType | undefined;
  fields: CreateTableViewSet;
  hideModal: () => void;
  modalType: string;
}

const BaseSaveView = ({ context, fields, hideModal, modalType }: Props) => {
  const [createTableView, createTableViewResult] =
    api.endpoints.createTableView.useMutation();
  const {
    isError: createTableViewError,
    isSuccess: createTableViewSuccess,
    isLoading,
    data,
  } = createTableViewResult;

  const [name, setName] = useState<Nullable<string>>('');
  const intl = useIntl();

  useEffect(() => {
    if (createTableViewError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'BaseSaveView.saveTableViewFailed',
          defaultMessage: 'Save table view failed. Please try again.',
          description: 'Error message when saving a table view fails',
        }),
      );
      hideModal();
    }
  }, [createTableViewError]);

  useEffect(() => {
    if (createTableViewSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'BaseSaveView.saveTableViewSuccess',
          defaultMessage: 'Table View has been successfully saved.',
          description: 'Success message when table view is saved',
        }),
      );
      trackSegment('Saving table view', data);
      hideModal();
    }
  }, [createTableViewSuccess]);

  const handleSave = (name: string) => {
    createTableView({ context, name, fields });
  };

  if (modalType !== MODAL_DOCUMENTS_COLUMN_FIELDS_SAVE) return null;

  return (
    <Modal
      title="Save View"
      onCancel={hideModal}
      isVisible={true}
      primaryAction={{
        text: intl.formatMessage({
          id: 'BaseSaveView.saveButton',
          defaultMessage: 'Save',
          description: 'Save button text for saving table view',
        }),
        onClick: () => handleSave(name ?? ''),
        level: 'primary' as const,
        disabled: isLoading,
      }}
    >
      <TextInput
        placeholder={intl.formatMessage({
          id: 'BaseSaveView.viewNamePlaceholder',
          defaultMessage: 'View name',
          description: 'Placeholder for table view name input',
        })}
        name="name"
        value={name}
        onChange={setName}
      ></TextInput>
    </Modal>
  );
};

export default BaseSaveView;
